// ─────────────────────────────────────────────────────────────────────────────
//  docs.config.js — o arquivo que você edita para mudar a estrutura do site.
//
//  Nada aqui é "descoberto por mágica": a ordem das seções, o menu do topo e
//  quais grupos aparecem em cada sidebar são declarados neste arquivo. O que É
//  automático: a lista de páginas dentro de cada grupo (vem do frontmatter dos
//  .mdx em content/) e a referência da API (vem de public/openapi.json).
// ─────────────────────────────────────────────────────────────────────────────

import { type Locales, type NavSection, type Site } from "./types/content.ts";

export const site = {
  title: "Infotravel API",
  description:
    "Documentação da API Infotravel: guias, referência de endpoints, Vibecoding, Skills, MCP, Prod Gate e Playground.",
  // Link do logotipo no canto esquerdo da subnav.
  homeUrl: "https://infotravel.com.br",
  // Caminho do spec OpenAPI servido estaticamente. Trocar este arquivo
  // atualiza toda a API Reference sem rebuild.
  //
  // Relativo de propósito: uma URL absoluta de localhost funciona na máquina de
  // quem escreveu e quebra em produção. É o mesmo arquivo que o /api/mcp lê —
  // atualize os dois com `npm run openapi:pull <url>`.
  openapiUrl: "/openapi.json",
} satisfies Site;

// ── Valores compartilhados ───────────────────────────────────────────────────
// `sandbox`, `endpoints` e `examples` moram em shared/constants.ts, que não
// importa nada e por isso atravessa os três programas do repositório (site,
// servidor e scripts). Continuam saindo daqui para quem já os importava.
export { endpoints, examples, sandbox } from "./shared/constants.ts";

export const locales = {
  default: "pt",
  // `fallback` decide o que mostrar quando uma página não foi traduzida.
  fallback: "pt",
  available: [
    { code: "pt", label: "PT", name: "Português" },
    { code: "en", label: "EN", name: "English" },
    { code: "es", label: "ES", name: "Español" },
  ],
} satisfies Locales;

// ── Menu do topo ─────────────────────────────────────────────────────────────
// Cada entrada vira um botão na subnav. `type` decide como a seção se comporta:
//
//   "guides"  → páginas .mdx de content/<locale>/<id>/, com sidebar
//   "openapi" → referência gerada do openapi.json, com sidebar por tag
//   "page"    → uma única página .mdx, largura total, sem sidebar
//
// `sidebar` (só para type: "guides") lista os grupos na ordem em que aparecem.
// O campo `group` do frontmatter de cada .mdx diz a qual grupo ele pertence.
//
// ── Desligar uma seção sem apagar nada ──────────────────────────────────────
//
//   enabled: false  → fora do ar. Some do menu e da busca, o link direto
//                     redireciona para a home, e os <Card> que apontam para ela
//                     deixam de renderizar. O .mdx continua no repositório.
//
//   hidden: true    → fora do menu e da busca, mas o link direto FUNCIONA.
//                     Para publicar algo em rascunho e mandar a URL para
//                     alguém revisar antes de anunciar.
//
// Reativar é apagar a linha. Nenhum conteúdo se perde nos dois casos.
export const nav: NavSection[] = [
  {
    id: "docs",
    label: { pt: "Docs", en: "Docs", es: "Docs" },
    icon: "book",
    type: "guides",
    // Um item com `groups` é um grupo-pai: ele não tem páginas próprias, só
    // grupos dentro, e a sidebar o desenha recolhível. Um item com `id` sozinho
    // é um grupo folha, que lista as páginas cujo `group` casa com esse id.
    //
    // O ramo que contém a página aberta começa expandido; os outros, fechados.
    // `collapsed: false` num pai força ele a abrir mesmo sem a página ativa
    // dentro.
    sidebar: [
      { id: "comecar", label: { pt: "Começar", en: "Get started", es: "Empezar" } },
      { id: "guias", label: { pt: "Guias", en: "Guides", es: "Guías" } },
    ],
  },
  {
    id: "api",
    label: { pt: "API Reference", en: "API Reference", es: "API Reference" },
    icon: "code",
    type: "openapi",
    // Rótulo de cada endpoint no índice lateral:
    //   "summary" (padrão) → o nome do endpoint, vindo do spec
    //   "path"             → a URL crua
    operationLabel: "summary",
    // Agrupa os endpoints de uma tag pelo recurso no path (hotel, flight,
    // circuit…). O prefixo comum é detectado sozinho. false = lista plana.
    groupOperations: true,
    // ── Tags ──────────────────────────────────────────────────────────────────
    // ATENÇÃO: as strings abaixo são CHAVES, não texto de tela. Precisam ser
    // idênticas às tags do spec, que no backend vêm de
    // @Operation(tags = {"..."}) e são escritas em inglês.
    //
    // Não traduza aqui. O spec inteiro é redigido em inglês (summary,
    // description, parâmetros); um grupo "Disponibilidade" contendo
    // "Hotel Availability page (by searchId)" ficaria incoerente. A tradução do
    // rótulo exibido fica em `tagLabels`, logo abaixo.
    //
    // Ordem na sidebar. Tag do spec que não estiver aqui vai para o fim, em
    // ordem alfabética — endpoint novo no backend nunca some por esquecimento.
    tagOrder: [
      "Authentication",
      "Availability",
      "CheckRate",
      "Cart",
      "Booking",
      "Rebooking",
      "Payment",
      "Backoffice",
      "Financial",
      "FinancialPosting",
      "Invoice",
      "Billing",
      "Company",
      "Corp",
      "User",
      "Utility",
      "Package",
      "Service",
      "PointRule",
      "Search Engine",
      "B2C",
      "OtaSite",
      "Whitelabel",
      "ChatBot",
    ],
    // Agrupamento visual das tags na sidebar. Tags fora daqui caem em "Outros".
    groups: [
      {
        label: { pt: "Autenticação & busca", en: "Auth & search", es: "Auth & búsqueda" },
        tags: ["Authentication", "Availability", "CheckRate", "Cart"],
      },
      {
        label: { pt: "Reservas & operação", en: "Bookings & ops", es: "Reservas & operación" },
        tags: ["Booking", "Rebooking", "Payment", "Backoffice"],
      },
      {
        label: { pt: "Financeiro", en: "Finance", es: "Financiero" },
        tags: ["Financial", "FinancialPosting", "Invoice", "Billing"],
      },
      {
        label: { pt: "Plataforma", en: "Platform", es: "Plataforma" },
        tags: ["Company", "Corp", "User", "Utility", "Package", "Service", "PointRule"],
      },
      {
        label: { pt: "Canais & motores", en: "Channels & engines", es: "Canales & motores" },
        tags: ["Search Engine", "B2C", "OtaSite", "Whitelabel", "ChatBot"],
      },
    ],
    // Rótulo exibido para cada tag (título da página e item da sidebar). A chave
    // é o nome cru do spec; o valor é o que o leitor vê. Tag sem entrada aqui
    // aparece com o nome do spec, o que é um fallback aceitável.
    tagLabels: {
      pt: {
        Authentication: "Autenticação",
        Availability: "Disponibilidade",
        CheckRate: "Check Rate",
        Cart: "Carrinho",
        Booking: "Reservas",
        Rebooking: "Rebooking",
        Payment: "Pagamentos",
        Backoffice: "Backoffice",
        Financial: "Financeiro",
        FinancialPosting: "Lançamentos financeiros",
        Invoice: "Notas fiscais",
        Billing: "Faturamento",
        Company: "Empresas",
        Corp: "Corporativo",
        // "Usuários" sozinho leva o leitor a achar que é o usuário da API. Estes
        // endpoints são o consumidor final do portal B2C — o `clientId` da
        // reserva. Quem autentica a integração é POST /authenticate.
        User: "Usuários do B2C",
        Utility: "Utilitários",
        Package: "Pacotes",
        Service: "Serviços",
        PointRule: "Regras de pontos",
        "Search Engine": "Search Engine",
        B2C: "B2C",
        OtaSite: "OTA Site",
        Whitelabel: "White Label (tenant)",
        ChatBot: "ChatBot",
      },
      // Só as tags cuja grafia crua do spec fica ruim na tela. As demais caem no
      // próprio nome, que já está em inglês.
      en: {
        CheckRate: "Check Rate",
        FinancialPosting: "Financial Posting",
        PointRule: "Point Rule",
        OtaSite: "OTA Site",
        Whitelabel: "White Label (tenant)",
        User: "B2C Users",
        Corp: "Corporate",
      },
      es: {
        Authentication: "Autenticación",
        Availability: "Disponibilidad",
        CheckRate: "Check Rate",
        Cart: "Carrito",
        Booking: "Reservas",
        Rebooking: "Rebooking",
        Payment: "Pagos",
        Backoffice: "Backoffice",
        Financial: "Financiero",
        FinancialPosting: "Asientos financieros",
        Invoice: "Facturas",
        Billing: "Facturación",
        Company: "Empresas",
        Corp: "Corporativo",
        User: "Usuarios del B2C",
        Utility: "Utilidades",
        Package: "Paquetes",
        Service: "Servicios",
        PointRule: "Reglas de puntos",
        "Search Engine": "Search Engine",
        B2C: "B2C",
        OtaSite: "OTA Site",
        Whitelabel: "White Label (tenant)",
        ChatBot: "ChatBot",
      },
    },
  },
  // ── B2C ────────────────────────────────────────────────────────────────────
  // A integração do lado do cliente: o que roda no site dele, não no servidor.
  // Veio do repositório front-documentation, que era um Docusaurus separado
  // publicado no GitHub Pages — as rotas antigas redirecionam para cá.
  //
  // Seção própria, e não um grupo dentro de "Docs", porque o leitor é outro: no
  // Docs está quem consome a API por HTTP; aqui está quem embute um widget e um
  // SDK numa página.
  {
    id: "b2c",
    label: "B2C",
    icon: "globe",
    type: "guides",
    sidebar: [
      { id: "visao-geral", label: { pt: "Visão geral", en: "Overview", es: "Visión general" } },
      // O Motor V1 fica DENTRO de Integrações, e não ao lado: o motor é uma das
      // integrações de front-end, junto com banners, SDK de login e domínio
      // personalizado — é assim que a visão geral os apresenta e é assim que o
      // CONTEXT.md os agrupa. Ele entra recolhido porque são 14 páginas, que
      // abertas empurrariam as outras três para fora da tela.
      {
        id: "integracoes",
        label: { pt: "Integrações", en: "Integrations", es: "Integraciones" },
        groups: [{ id: "motor", label: { pt: "Motor V1", en: "Motor V1", es: "Motor V1" } }],
      },
    ],
  },
  // Playground desligado até o console real existir. content/pt/playground.mdx
  // fica onde está; apague `enabled: false` para religar.
  {
    id: "playground",
    label: { pt: "Playground", en: "Playground", es: "Playground" },
    icon: "terminal",
    type: "page",
    enabled: false,
  },
  { id: "downloads", label: { pt: "Downloads", en: "Downloads", es: "Descargas" }, icon: "download", type: "page" },
  {
    id: "vibecoding",
    label: { pt: "Vibecoding", en: "Vibecoding", es: "Vibecoding" },
    icon: "sparkles",
    type: "page",
    ai: true,
    enabled: false,
  },
  // Skills desligado: a página descreve uma DSL e três skills que não existem em
  // nenhum projeto. content/pt/skills.mdx está em `draft: true` com as notas do que
  // precisaria ser real; apague `enabled: false` quando houver o que documentar.
  {
    id: "skills",
    label: { pt: "Skills", en: "Skills", es: "Skills" },
    icon: "grid",
    type: "page",
    ai: true,
    enabled: false,
  },
  { id: "mcp", label: "MCP", icon: "plug", type: "page", ai: true },
  {
    id: "prg",
    label: { pt: "Production Readiness", en: "Production Readiness", es: "Production Readiness" },
    icon: "gauge",
    type: "page",
  },
  // Changelog fora do ar por enquanto: some do menu e da busca, e o link direto
  // redireciona para a home. O content/pt/changelog.mdx continua no repositório.
  { id: "changelog", label: "Changelog", icon: "history", type: "page", enabled: false },
];

// ── Strings da interface ─────────────────────────────────────────────────────
// Só o chrome do site. O conteúdo das páginas vive nos .mdx.
export const ui = {
  pt: {
    search: "Buscar",
    searchPlaceholder: "Buscar…",
    nothingFound: "Nada encontrado.",
    onThisPage: "Nesta página",
    navigation: "Navegação",
    backToSite: "Voltar ao site",
    noParams: "Sem parâmetros.",
    params: "Parâmetros",
    requestBody: "Corpo da requisição",
    request: "Request",
    response: "Response",
    statusHere: "Status desta chamada",
    bestPractice: "Boa prática",
    rateLimit: "Rate limit",
    baseUrl: "BASE URL",
    apiReference: "API Reference",
    notFound: "Página não encontrada.",
    untranslated: "Esta página ainda não foi traduzida — exibindo a versão em português.",
    loadingSpec: "Carregando especificação…",
    specError: "Não foi possível carregar o OpenAPI.",
    required: "obrigatório",
    deprecated: "descontinuado",
    expand: "Expandir",
    collapse: "Recolher",
    lines: "{n} linhas",
    fieldName: "Nome",
    fieldType: "Tipo",
    fieldRequired: "Req.",
    fieldDescription: "Descrição",
    responseFields: "Campos da resposta",
    requestFields: "Campos do corpo",
    themeDark: "Ativar tema escuro",
    themeLight: "Ativar tema claro",
  },
  en: {
    search: "Search",
    searchPlaceholder: "Search…",
    nothingFound: "Nothing found.",
    onThisPage: "On this page",
    navigation: "Navigation",
    backToSite: "Back to site",
    noParams: "No parameters.",
    params: "Parameters",
    requestBody: "Request body",
    request: "Request",
    response: "Response",
    statusHere: "Statuses for this call",
    bestPractice: "Best practice",
    rateLimit: "Rate limit",
    baseUrl: "BASE URL",
    apiReference: "API Reference",
    notFound: "Page not found.",
    untranslated: "This page has not been translated yet — showing the Portuguese version.",
    loadingSpec: "Loading specification…",
    specError: "Could not load the OpenAPI document.",
    required: "required",
    deprecated: "deprecated",
    expand: "Expand",
    collapse: "Collapse",
    lines: "{n} lines",
    fieldName: "Name",
    fieldType: "Type",
    fieldRequired: "Req.",
    fieldDescription: "Description",
    responseFields: "Response fields",
    requestFields: "Request body fields",
    themeDark: "Switch to dark theme",
    themeLight: "Switch to light theme",
  },
  es: {
    search: "Buscar",
    searchPlaceholder: "Buscar…",
    nothingFound: "Nada encontrado.",
    onThisPage: "En esta página",
    navigation: "Navegación",
    backToSite: "Volver al sitio",
    noParams: "Sin parámetros.",
    params: "Parámetros",
    requestBody: "Cuerpo de la petición",
    request: "Request",
    response: "Response",
    statusHere: "Estados de esta llamada",
    bestPractice: "Buena práctica",
    rateLimit: "Rate limit",
    baseUrl: "BASE URL",
    apiReference: "API Reference",
    notFound: "Página no encontrada.",
    untranslated: "Esta página aún no fue traducida — mostrando la versión en portugués.",
    loadingSpec: "Cargando especificación…",
    specError: "No se pudo cargar el OpenAPI.",
    required: "obligatorio",
    deprecated: "obsoleto",
    expand: "Expandir",
    collapse: "Contraer",
    lines: "{n} líneas",
    fieldName: "Nombre",
    fieldType: "Tipo",
    fieldRequired: "Req.",
    fieldDescription: "Descripción",
    responseFields: "Campos de la respuesta",
    requestFields: "Campos del cuerpo",
    themeDark: "Activar tema oscuro",
    themeLight: "Activar tema claro",
  },
};

export default { site, locales, nav, ui };
